/**
 * Which timezone a stop's clock is read in.
 *
 * Context: `cities.timezone` defaults to `'UTC'` and, until the backfill runs,
 * EVERY row still holds it. A route crossing the border therefore has no usable
 * per-stop zone yet, and the server-rendered surfaces (manifest, ticket,
 * road-pickup report) all fall back to the tenant's zone — which is
 * `America/Chicago` for Pegasos and so one hour wrong for every Mexican stop
 * from March to November, since Mexico dropped DST in 2022.
 *
 * The resolution order below is what lets those surfaces move to per-stop zones
 * incrementally: a backfilled city takes effect immediately, an un-backfilled
 * one keeps today's behaviour instead of suddenly rendering UTC.
 */

import { DEFAULT_TIMEZONE, isValidTimezone } from "../timezone-utils";

/**
 * `'UTC'` in `cities.timezone` means "not backfilled yet", NOT "this city runs
 * on UTC".
 *
 * This is a stated convention, not an inference: UTC is a perfectly valid zone,
 * but it is also this column's default, and no city RutaLink serves is actually
 * on it. Treating it as absent is what makes the fallback chain safe to deploy
 * before the backfill has run. If a genuinely-UTC city ever exists, it needs an
 * explicit equivalent zone (e.g. `Atlantic/Reykjavik`) rather than the default.
 */
export function isUnsetTimezone(tz: string | null | undefined): boolean {
  return !tz || tz === "UTC" || !isValidTimezone(tz);
}

/**
 * Resolve the timezone a stop's time should be rendered in:
 *
 *   the stop's own city → the route origin's city → the tenant → DEFAULT_TIMEZONE
 *
 * The `'UTC'`-means-unset convention applies ONLY to the two CITY candidates,
 * because `cities.timezone` is the column that defaults to it. `tenants.timezone`
 * has no such default — a tenant set to UTC was set there deliberately, and
 * silently rewriting that to America/Chicago would be inventing a zone for them.
 * An invalid tenant zone is still skipped, since nothing can format with it.
 */
export function resolveStopTimezone(
  cityTimezone: string | null | undefined,
  originCityTimezone: string | null | undefined,
  tenantTimezone: string | null | undefined,
): string {
  for (const candidate of [cityTimezone, originCityTimezone]) {
    if (!isUnsetTimezone(candidate)) return candidate as string;
  }
  if (tenantTimezone && isValidTimezone(tenantTimezone)) return tenantTimezone;
  return DEFAULT_TIMEZONE;
}
